import { Container, Graphics } from 'pixi.js'

export interface WinPresenterConfig {
  reelCount: number
  rowCount: number
  symbolWidth: number
  symbolHeight: number
  reelGap: number
  pulseDuration?: number // total pulse animation ms (default 1500 = 3 cycles × 500ms)
  pulseScale?: number // max scale factor during pulse (default 1.1)
  dimAlpha?: number // alpha for non-winning symbols (default 0.3)
  pulseCount?: number // number of pulse cycles (default 3)
  paylineColors?: number[] // colors for payline overlay lines
}

export interface WinDisplayData {
  type: 'payline' | 'ways' | 'cluster'
  positions: { reel: number; row: number }[]
  paylineId?: number // required for payline type
}

export interface WinAnimationFrame {
  /** Scale factor for winning symbols this frame */
  pulseScale: number
  /** Whether the presentation is currently playing */
  isActive: boolean
  /** Set of "reel:row" keys identifying winning positions */
  winPositions: ReadonlySet<string>
  /** Whether the full animation sequence has completed */
  isComplete: boolean
}

const DEFAULT_PAYLINE_COLORS: number[] = [
  0xff0000, 0x00ff00, 0x0000ff, 0xffff00, 0xff00ff, 0x00ffff, 0xff8000, 0x8000ff, 0x00ff80,
  0xff0080, 0xffffff, 0x80ff00, 0x0080ff, 0xff8080, 0x80ff80, 0x8080ff, 0xffff80, 0xff80ff,
  0x80ffff, 0xc0c0c0, 0xff4040, 0x40ff40, 0x4040ff, 0xffff40, 0xff40ff,
]

/**
 * WinPresenter renders visual feedback for slot wins:
 *   - Payline overlays (colored lines connecting winning positions)
 *   - Glow highlights for ways-to-win / cluster pay wins
 *   - Pulse animation data for winning symbols (scale 1 → 1.1 → 1, 3× over 1.5s)
 *   - Dim alpha for non-winning symbols
 *
 * Like SpinAnimator, this is a standalone class that returns per-frame data.
 * The caller (SlotGame.vue) applies scale/alpha to ReelContainer symbols.
 */
export class WinPresenter {
  readonly container: Container

  private readonly _reelCount: number
  private readonly _rowCount: number
  private readonly _symbolWidth: number
  private readonly _symbolHeight: number
  private readonly _reelGap: number
  private readonly _pulseDuration: number
  private readonly _pulseScale: number
  private readonly _dimAlpha: number
  private readonly _pulseCount: number
  private readonly _paylineColors: number[]

  private winPositions: Set<string> = new Set()
  private active: boolean = false
  private elapsed: number = 0

  constructor(config: WinPresenterConfig) {
    this._reelCount = config.reelCount
    this._rowCount = config.rowCount
    this._symbolWidth = config.symbolWidth
    this._symbolHeight = config.symbolHeight
    this._reelGap = config.reelGap
    this._pulseDuration = config.pulseDuration ?? 1500
    this._pulseScale = config.pulseScale ?? 1.1
    this._dimAlpha = config.dimAlpha ?? 0.3
    this._pulseCount = config.pulseCount ?? 3
    this._paylineColors = config.paylineColors ?? DEFAULT_PAYLINE_COLORS

    this.container = new Container()
    this.container.name = 'win-presenter'
  }

  /** Show win presentation for the given wins */
  showWins(wins: WinDisplayData[]): void {
    this.clear()
    if (wins.length === 0) return

    this.active = true
    this.elapsed = 0

    // Collect all winning positions
    for (const win of wins) {
      for (const pos of win.positions) {
        this.winPositions.add(`${pos.reel}:${pos.row}`)
      }
    }

    // Draw visual overlays per win
    for (const win of wins) {
      if (win.type === 'payline' && win.paylineId !== undefined) {
        this.drawPaylineOverlay(win.positions, win.paylineId)
      } else {
        this.drawHighlightOverlay(win.positions)
      }
    }
  }

  /**
   * Advance the animation by deltaMs.
   * Returns per-frame data for the caller to apply to symbol display objects.
   */
  update(deltaMs: number): WinAnimationFrame {
    if (!this.active) {
      return {
        pulseScale: 1,
        isActive: false,
        winPositions: this.winPositions,
        isComplete: true,
      }
    }

    this.elapsed += deltaMs

    if (this.elapsed >= this._pulseDuration) {
      this.active = false
      return {
        pulseScale: 1,
        isActive: false,
        winPositions: this.winPositions,
        isComplete: true,
      }
    }

    // N pulse cycles over pulseDuration
    const cycleDuration = this._pulseDuration / this._pulseCount
    const cycleProgress = (this.elapsed % cycleDuration) / cycleDuration

    // sin(0→π) per cycle: smooth 0 → 1 → 0
    const pulseAmount = Math.sin(cycleProgress * Math.PI)
    const pulseScale = 1 + (this._pulseScale - 1) * pulseAmount

    return {
      pulseScale,
      isActive: true,
      winPositions: this.winPositions,
      isComplete: false,
    }
  }

  /** Whether the win presentation is currently playing */
  get isActive(): boolean {
    return this.active
  }

  /** Alpha for non-winning symbols during active presentation */
  get dimAlpha(): number {
    return this._dimAlpha
  }

  /** Configured pulse scale maximum */
  get pulseScale(): number {
    return this._pulseScale
  }

  /** Configured pulse count */
  get pulseCount(): number {
    return this._pulseCount
  }

  /** Configured pulse duration */
  get pulseDuration(): number {
    return this._pulseDuration
  }

  /** Get a copy of winning positions */
  getWinPositions(): Set<string> {
    return new Set(this.winPositions)
  }

  /**
   * Get the correct alpha for a symbol at a given position.
   * During active presentation: winners = 1.0, non-winners = dimAlpha.
   * When inactive: all = 1.0.
   */
  getAlpha(reel: number, row: number): number {
    if (!this.active && this.winPositions.size === 0) return 1
    if (this.winPositions.size === 0) return 1
    return this.winPositions.has(`${reel}:${row}`) ? 1.0 : this._dimAlpha
  }

  /** Check if a position is a winning position */
  isWinPosition(reel: number, row: number): boolean {
    return this.winPositions.has(`${reel}:${row}`)
  }

  /** Clear all win visuals and reset state */
  clear(): void {
    this.active = false
    this.elapsed = 0
    this.winPositions.clear()

    // Remove all overlay graphics
    while (this.container.children.length > 0) {
      const child = this.container.children[0]
      this.container.removeChild(child)
      child.destroy({ children: true })
    }
  }

  /** Clean up all resources */
  destroy(): void {
    this.clear()
    this.container.destroy({ children: true })
  }

  /** Get the center pixel position of a symbol cell */
  private getSymbolCenter(reel: number, row: number): { x: number; y: number } {
    return {
      x: reel * (this._symbolWidth + this._reelGap) + this._symbolWidth / 2,
      y: row * this._symbolHeight + this._symbolHeight / 2,
    }
  }

  /** Draw a colored line through winning symbol centers for a payline */
  private drawPaylineOverlay(
    positions: { reel: number; row: number }[],
    paylineId: number,
  ): void {
    if (positions.length < 2) return

    const color = this._paylineColors[paylineId % this._paylineColors.length]
    const line = new Graphics()
    line.name = `payline-${paylineId}`

    // Sort positions left-to-right by reel
    const sorted = [...positions].sort((a, b) => a.reel - b.reel)

    const first = this.getSymbolCenter(sorted[0].reel, sorted[0].row)
    line.lineStyle(3, color, 0.8)
    line.moveTo(first.x, first.y)

    for (let i = 1; i < sorted.length; i++) {
      const center = this.getSymbolCenter(sorted[i].reel, sorted[i].row)
      line.lineTo(center.x, center.y)
    }

    this.container.addChild(line)
  }

  /** Draw glow highlight rectangles around positions (ways-to-win / cluster pays) */
  private drawHighlightOverlay(positions: { reel: number; row: number }[]): void {
    const glow = new Graphics()
    glow.name = 'win-highlight'

    for (const pos of positions) {
      const x = pos.reel * (this._symbolWidth + this._reelGap)
      const y = pos.row * this._symbolHeight

      glow.lineStyle(2, 0xffd700, 0.9)
      glow.beginFill(0xffd700, 0.15)
      glow.drawRoundedRect(x + 1, y + 1, this._symbolWidth - 2, this._symbolHeight - 2, 4)
      glow.endFill()
    }

    this.container.addChild(glow)
  }
}
